import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { DataSourceBadge } from "components/badges/DataSourceBadge"
import { TablePagination } from "components/paginations/TablePagination"

import { balanceService } from "services/balance/balance.service"
import { BalanceTerminalsResponse, BalanceTxType } from "services/balance/type"

import { isAuthorityUnavailableError } from "util/api"
import { centsToUsdInput, formatFiatCurrency, keyFor, parseNonNegInt, parseUsdToCents } from "util/formatter"

import { Draft, TerminalRow } from "./types"

const headerColumns: { key: string; label: string; align: "left" | "right"; sortable: boolean }[] = [
  { key: "terminal_name", label: "Terminal", align: "left", sortable: true },
  { key: "volume_usd_cents", label: "Volume (USD)", align: "right", sortable: true },
  { key: "tx_count", label: "Tx Count", align: "right", sortable: true },
  { key: "percent_bps", label: "Fee (bps)", align: "right", sortable: true },
  { key: "flat_usd_cents", label: "Flat ($)", align: "right", sortable: true },
  { key: "rolling_reserve_bps", label: "RR (bps)", align: "right", sortable: true },
  { key: "settlement_fee_bps", label: "Sttl Fee (bps)", align: "right", sortable: true },
  { key: "fee", label: "Fee", align: "right", sortable: false },
  { key: "fee_total_usd_cents", label: "Fee Total (USD)", align: "right", sortable: true },
  { key: "rolling_reserve_usd_cents", label: "RR (USD)", align: "right", sortable: true },
  { key: "available_usd_cents", label: "Available (USD)", align: "right", sortable: true },
  { key: "actions", label: "Actions", align: "right", sortable: false }
]

const editableFields: { draftKey: keyof Draft; inputMode: "numeric" | "decimal" }[] = [
  { draftKey: "percent_bps", inputMode: "numeric" },
  { draftKey: "flat_usd", inputMode: "decimal" },
  { draftKey: "rolling_reserve_bps", inputMode: "numeric" },
  { draftKey: "settlement_fee_bps", inputMode: "numeric" }
]

type Props = {
  processor: string
  payment_method: string
  txType: BalanceTxType
  filters?: { start?: string; end?: string }
}

export const BalanceTerminalsEditorTable: FC<Props> = ({ processor, payment_method, txType, filters }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [loading, setLoading] = useState(false)

  const [rows, setRows] = useState<TerminalRow[]>([])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(1)

  const [orderBy, setOrderBy] = useState<string>("terminal_name")
  const [order, setOrder] = useState<"asc" | "desc">("asc")

  const [draft, setDraft] = useState<Record<string, Draft>>({})

  // SOFT-559: data-source marker + a fail-closed 503 state that keeps the already-rendered rows.
  const [dataSource, setDataSource] = useState<string | undefined>(undefined)
  const [loadError, setLoadError] = useState<null | "unavailable" | "generic">(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = (await balanceService.getTerminalTerminalsTable(processor, payment_method, {
        tx_type: txType,
        start: filters?.start,
        end: filters?.end,
        page,
        pageSize,
        sortRow: orderBy,
        sortOrder: order
      })) as BalanceTerminalsResponse | undefined

      const data = res?.data ?? []
      setRows(data)
      setTotalPages(res?.total_pages ?? 1)
      setDataSource(res?.data_source)

      setDraft((prev) => {
        const next = { ...prev }
        for (const r of data) {
          const k = keyFor(r.route_id)
          if (!next[k]) {
            next[k] = {
              percent_bps: String(r.percent_bps ?? 0),
              flat_usd: centsToUsdInput(r.flat_usd_cents ?? 0),
              rolling_reserve_bps: String(r.rolling_reserve_bps ?? 0),
              settlement_fee_bps: String(r.settlement_fee_bps ?? 0)
            }
          }
        }
        return next
      })
    } catch (e: any) {
      // SOFT-559: fail-closed — keep the current rows on a 503 (FS-era window, FS down) and show a
      // retry affordance instead of a generic toast + a blanked table.
      setLoadError(isAuthorityUnavailableError(e) ? "unavailable" : "generic")
    } finally {
      setLoading(false)
    }
  }, [processor, payment_method, txType, filters?.start, filters?.end, page, pageSize, orderBy, order])

  useEffect(() => {
    load()
  }, [load])

  const onJump = useCallback(
    (jumpTo: number) => {
      if (!jumpTo) return
      setPage(Math.min(Math.max(1, jumpTo), totalPages))
    },
    [totalPages]
  )

  const onChange = (routeId: number, patch: Partial<Draft>) => {
    const k = keyFor(routeId)
    setDraft((prev) => ({
      ...prev,
      [k]: { ...(prev[k] ?? { percent_bps: "0", flat_usd: "0.00", rolling_reserve_bps: "0", settlement_fee_bps: "0" }), ...patch }
    }))
  }

  const isDirty = (routeId: number, percentBps: number, flatCents: number, rrBps: number, sfBps: number) => {
    const d = draft[keyFor(routeId)]
    if (!d) return false
    const bpsNow = Number(d.percent_bps)
    const centsNow = Math.round(Number(d.flat_usd) * 100)
    const rrNow = Number(d.rolling_reserve_bps)
    const sfNow = Number(d.settlement_fee_bps)
    return bpsNow !== percentBps || centsNow !== flatCents || rrNow !== rrBps || sfNow !== sfBps
  }

  const onSave = async (row: TerminalRow) => {
    const d = draft[keyFor(row.route_id)]
    if (!d) return

    const parsedBps = parseNonNegInt(d.percent_bps)
    if (parsedBps === null) return toast.error("Percent (bps) is required")
    if (!Number.isInteger(parsedBps) || parsedBps < 0) return toast.error("Percent must be a non-negative integer (bps)")

    const parsedFlat = parseUsdToCents(d.flat_usd)
    if (parsedFlat === null) return toast.error("Flat fee is required")
    if (!Number.isInteger(parsedFlat) || parsedFlat < 0) return toast.error("Flat fee must be a non-negative number")

    const parsedRr = parseNonNegInt(d.rolling_reserve_bps)
    if (parsedRr === null) return toast.error("Rolling Reserve (bps) is required")

    const parsedSf = parseNonNegInt(d.settlement_fee_bps)
    if (parsedSf === null) return toast.error("Settlement Fee (bps) is required")

    if (
      parsedBps === row.percent_bps &&
      parsedFlat === row.flat_usd_cents &&
      parsedRr === row.rolling_reserve_bps &&
      parsedSf === row.settlement_fee_bps
    )
      return

    setLoading(true)
    try {
      const res = await balanceService.upsertTerminalFee({
        tx_type: txType,
        route_id: row.route_id,
        percent_bps: parsedBps,
        flat_usd_cents: parsedFlat,
        rolling_reserve_bps: parsedRr,
        settlement_fee_bps: parsedSf
      })
      if (!res) return
      toast.success("Fee updated")
      await load()
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to update fee")
    } finally {
      setLoading(false)
    }
  }

  const formatFullFee = (bps: string, flatUsd: string) => {
    const bpsNum = Number(bps)
    const flatNum = Number(flatUsd)

    const parts: string[] = []
    if (Number.isFinite(bpsNum) && bpsNum > 0) parts.push(`${(bpsNum / 100).toFixed(2)}%`)
    if (Number.isFinite(flatNum) && flatNum > 0) parts.push(`$${flatNum.toFixed(2)}`)
    return parts.length ? parts.join(" + ") : "–"
  }

  const cell = "py-3 px-3 text-sm text-[rgba(6,7,21,1)] dark:text-white"
  const inputClass = clsx(
    "w-24 rounded-lg border px-3 py-2 text-right text-sm outline-none transition",
    "bg-white dark:bg-[#0C0C1A]",
    "border-[rgba(139,92,246,0.4)] focus:border-[rgba(139,92,246,1)]",
    "text-[rgba(6,7,21,1)] dark:text-white",
    loading && "opacity-60 cursor-not-allowed"
  )
  const fiatCell = (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))

  const sort = (next: string) => {
    if (next === orderBy) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"))
      setPage(1)
      return
    }
    setOrderBy(next)
    setOrder("asc")
    setPage(1)
  }

  return (
    <div className="rounded-xl border overflow-x-auto border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
      {dataSource || loadError ? (
        <div className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-[#0C0C1A]">
          <DataSourceBadge source={dataSource} />
          {loadError ? (
            <span className={clsx("text-sm", loadError === "unavailable" ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400")}>
              {loadError === "unavailable" ? t("table.loadFailedUnavailable") : t("table.loadFailed")}
            </span>
          ) : null}
        </div>
      ) : null}

      <table className="w-full min-w-[1400px] bg-white dark:bg-[#0C0C1A]">
        <thead className="bg-[rgba(139,92,246,0.06)] dark:bg-[rgba(139,92,246,0.08)]">
          <tr className="text-xs uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70">
            {headerColumns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  "py-3 px-3",
                  col.align === "left" ? "text-left" : "text-right",
                  col.sortable && "cursor-pointer select-none"
                )}
                onClick={col.sortable ? () => sort(col.key) : undefined}
              >
                {col.label} {col.sortable && orderBy === col.key ? (order === "asc" ? "▲" : "▼") : ""}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {!rows.length ? (
            <tr>
              <td colSpan={12} className="py-10 text-center">
                <div className="text-sm text-[rgba(6,7,21,0.6)] dark:text-white/60">
                  {loading ? t("common.loading") : t("balance.noTerminals")}
                </div>
              </td>
            </tr>
          ) : null}

          {rows.map((r) => {
            const d = draft[keyFor(r.route_id)] ?? {
              percent_bps: String(r.percent_bps ?? 0),
              flat_usd: centsToUsdInput(r.flat_usd_cents ?? 0),
              rolling_reserve_bps: String(r.rolling_reserve_bps ?? 0),
              settlement_fee_bps: String(r.settlement_fee_bps ?? 0)
            }
            const dirty = isDirty(r.route_id, r.percent_bps, r.flat_usd_cents, r.rolling_reserve_bps, r.settlement_fee_bps)

            return (
              <tr
                key={r.route_id}
                className={clsx(
                  "border-t border-[rgba(0,0,0,0.03)] dark:border-[rgba(255,255,255,0.06)]",
                  "hover:bg-[rgba(139,92,246,0.2)] hover:dark:bg-[rgba(139,92,246,0.10)]"
                )}
              >
                <td className={clsx(cell)}>{r.terminal_name}</td>

                <td className={clsx(cell, "text-right font-medium")}>{fiatCell(r.volume_usd_cents)}</td>

                <td className={clsx(cell, "text-right")}>{r.tx_count ?? 0}</td>

                {editableFields.map((f) => (
                  <td key={f.draftKey} className={clsx(cell, "text-right")}>
                    <input
                      className={inputClass}
                      value={d[f.draftKey]}
                      onChange={(e) => onChange(r.route_id, { [f.draftKey]: e.target.value })}
                      disabled={loading}
                      inputMode={f.inputMode}
                    />
                  </td>
                ))}

                <td className={clsx(cell, "text-right text-xs font-medium text-[rgba(6,7,21,0.7)] dark:text-white/70")}>
                  {formatFullFee(d.percent_bps, d.flat_usd)}
                </td>

                {[r.fee_total_usd_cents, r.rolling_reserve_usd_cents, r.available_usd_cents].map((v, i) => (
                  <td key={i} className={clsx(cell, "text-right font-medium")}>
                    {fiatCell(v)}
                  </td>
                ))}

                <td className={clsx(cell, "text-right")}>
                  <button
                    className={clsx(
                      "rounded-lg px-4 py-2 text-sm font-semibold transition",
                      dirty
                        ? "bg-[rgba(139,92,246,1)] text-white hover:bg-[rgba(139,92,246,0.9)]"
                        : "bg-[rgba(0,0,0,0.06)] text-[rgba(6,7,21,0.7)] dark:bg-white/10 dark:text-white/60",
                      loading && "opacity-60 cursor-not-allowed"
                    )}
                    onClick={() => onSave(r)}
                    disabled={loading || !dirty}
                    title={dirty ? "Save fee" : "No changes"}
                  >
                    {t("common.save")}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {rows.length ? (
        <div className="p-3 bg-white dark:bg-[#0C0C1A]">
          <TablePagination page={page} totalPages={totalPages} onJump={onJump} />
        </div>
      ) : null}
    </div>
  )
}
